import { Dispatch, useMemo, useState } from 'react'
import {
  Card,
  Text,
  Button,
  Icon,
  CardColors,
  Separator,
  Tooltip,
  Checkbox,
} from '@/ui'
import { DeleteStatement } from './DeleteStatementModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import {
  faCircle,
  faTrashCan,
  faUpload,
} from '@fortawesome/pro-regular-svg-icons'
import { faXmark, faCheck } from '@fortawesome/pro-solid-svg-icons'
// TODO: Migrate to V2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'
import { TypographyStyles } from '@/styles'
import { NotExpectedStateAction } from '@/features/onboarding/upload-statements-stage/types'

export const MissingStatementCard = ({
  month,
  yearMonth,
  financialAccountId,
  statements,
  notExpected,
  setNotExpectedValue,
}: {
  month: string
  yearMonth: string
  financialAccountId: number
  statements: UserDocument[]
  notExpected: boolean
  setNotExpectedValue: Dispatch<NotExpectedStateAction>
}) => {
  const [uploadedStatements, setUploadedStatements] = useState<UserDocument[]>(
    []
  )
  const [openUpload, setOpenUpload] = useState(false)
  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<UserDocument>()

  const setUploadedFile = ({
    doc,
  }: {
    fileName?: string
    id?: number
    doc?: UserDocument
  }) => {
    if (doc?.id && doc.statementMonth === yearMonth) {
      const statements = [...uploadedStatements, doc]
      setUploadedStatements(statements)
    }
  }

  const filteredStatements = useMemo(() => {
    return statements.filter(
      (statement) =>
        statement.statementMonth === yearMonth &&
        statement.statement?.statementFinancialAccounts?.some(
          (account) => account.financialAccountId === financialAccountId
        )
    )
  }, [financialAccountId, statements, yearMonth])

  const isCompleted =
    uploadedStatements.length >= 1 || Boolean(filteredStatements.length)

  const { cardColor, iconProps } = useMemo(() => {
    if (notExpected) {
      return {
        cardColor: CardColors.LEVEL_2,
        iconProps: {
          icon: faXmark,
          color: 'text-neutral-300',
        },
      }
    }

    if (isCompleted) {
      return {
        cardColor: CardColors.SUCCESS,
        iconProps: {
          icon: faCheck,
          color: 'text-green-500',
        },
      }
    }

    return {
      cardColor: CardColors.PRIORITY,
      iconProps: {
        icon: faCircle,
        color: 'text-neutral-500',
      },
    }
  }, [isCompleted, notExpected])

  return (
    <Card color={cardColor}>
      <div className="flex flex-col gap-3 w-full sm:flex-row sm:justify-between sm:items-center sm:gap-0">
        <div className="flex flex-row items-center flex-grow min-w-0 sm:mr-20">
          <Icon backgroundStyle="circle" size="2x" {...iconProps} />
          <Text variant={TypographyStyles.BODY_MD}>{month}</Text>
        </div>

        <div className="flex flex-col items-start w-full shrink-0 sm:w-auto sm:items-end sm:justify-end">
          <div className="w-full sm:w-auto">
            <Button
              onClick={() => {
                setOpenUpload(true)
              }}
              variant="secondary"
              className="flex items-center w-full sm:w-[120px]"
            >
              <Icon icon={faUpload} size="1x" className="mr-1" />
              Upload
            </Button>
          </div>
          <div className="flex flex-row items-center gap-2 mt-2">
            <Checkbox
              onCheckedChange={(checked) => {
                setNotExpectedValue({
                  value: checked,
                  monthYear: yearMonth,
                  financialAccountId,
                })
              }}
              checked={notExpected}
            />
            <Tooltip>
              <Tooltip.Trigger>
                <Text
                  className="border-b-2 border-green-50 border-dotted hover:cursor-pointer hover:text-brand active:text-neutral-500"
                  variant={TypographyStyles.BODY_SM}
                >
                  This account wasn’t open yet
                </Text>
              </Tooltip.Trigger>
              <Tooltip.Content header="This account wasn’t open yet">
                <Text variant={TypographyStyles.BODY_SM}>
                  Select this if you hadn’t opened the account yet and therefore
                  don’t have a statement for this month.
                </Text>
              </Tooltip.Content>
            </Tooltip>
          </div>
        </div>
      </div>

      {isCompleted && (
        <div className="ml-4 pt-2 sm:ml-16">
          <Separator />
          {filteredStatements.map((file: UserDocument) => (
            <div key={file.id} className="flex flex-row items-center gap-2">
              <Text className="min-w-0 break-all">{file.fileDescription}</Text>
              <Button
                onClick={() => {
                  setDeleteDocumentModalOpen(true)
                  setDocumentToDelete(file)
                }}
                iconLeft={faTrashCan}
                variant="discreet"
                aria-label={`Delete ${file.fileDescription}`}
              />
            </div>
          ))}
        </div>
      )}

      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={openUpload}
        close={() => setOpenUpload(false)}
        userFacing
        monthYear={yearMonth || undefined}
        financialAccountId={[financialAccountId]}
        title="Upload Statement"
        description="Upload statement for the selected account and month"
        subHeaderText=" "
        setUploadedFile={setUploadedFile}
      />

      <DeleteStatement
        document={documentToDelete}
        open={deleteDocumentModalOpen}
        close={() => setDeleteDocumentModalOpen(false)}
        updateDocumentStatements={() =>
          setUploadedStatements((docs: UserDocument[]) =>
            docs.filter((doc) => doc.id !== documentToDelete?.id)
          )
        }
      />
    </Card>
  )
}
